import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Theme palette keys usable as the card's accent / status colour. Kept to the
 * semantic slots so the card always tracks the active (and per-company) theme
 * instead of hardcoded hues.
 */
export type MobileCardAccent =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info';

export interface MobileEntityCardProps {
  /** Header band + default status colour (theme palette slot). Default 'primary'. */
  accent?: MobileCardAccent;
  /** Header band left content (e.g. a code / role). Truncates to one line. */
  headerLeft?: React.ReactNode;
  /** Header band right content (e.g. a date). */
  headerRight?: React.ReactNode;
  /** Small muted line above the title (e.g. email / "Registrado por…"). */
  eyebrow?: React.ReactNode;
  /** Primary title — wraps fully (no ellipsis, no mid-word break). */
  title: React.ReactNode;
  /** Soft-tinted status pill shown top-right of the body. */
  status?: { label: React.ReactNode; color?: MobileCardAccent };
  /** Optional body detail area rendered between the title and the footer. */
  children?: React.ReactNode;
  /** Footer left content (small meta). Footer only renders if left or right is set. */
  footerLeft?: React.ReactNode;
  /** Footer right content (CTA button / action icons). */
  footerRight?: React.ReactNode;
  /** Whole-card click (e.g. open detail). Interactive footer controls should stopPropagation. */
  onClick?: () => void;
}

/**
 * Shared mobile list card — the colour-banded card design used across the app's
 * mobile lists (guest visits, users, …). Header band + title/status body +
 * dashed divider + footer. All colours come from the theme palette.
 */
export const MobileEntityCard: React.FC<MobileEntityCardProps> = ({
  accent = 'primary',
  headerLeft,
  headerRight,
  eyebrow,
  title,
  status,
  children,
  footerLeft,
  footerRight,
  onClick,
}) => {
  const theme = useTheme();
  const accentPal = theme.palette[accent];
  const statusPal = status?.color ? theme.palette[status.color] : accentPal;
  const hasHeader = headerLeft != null || headerRight != null;
  const hasFooter = footerLeft != null || footerRight != null;

  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: '0 6px 20px rgba(16, 24, 64, 0.08)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:active': onClick ? { transform: 'scale(0.995)' } : undefined,
      }}
    >
      {hasHeader && (
        <Box
          sx={{
            bgcolor: accentPal.main,
            color: accentPal.contrastText,
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              fontWeight: 700,
              letterSpacing: 0.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {headerLeft}
          </Box>
          {headerRight != null && (
            <Box sx={{ flexShrink: 0, fontWeight: 600, fontSize: '0.875rem' }}>{headerRight}</Box>
          )}
        </Box>
      )}

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {eyebrow != null && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {eyebrow}
              </Typography>
            )}
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                lineHeight: 1.25,
                // Full title: wrap at word boundaries, never ellipsize or break mid-word.
                whiteSpace: 'normal',
                overflowWrap: 'normal',
                wordBreak: 'normal',
              }}
            >
              {title}
            </Typography>
          </Box>
          {status != null && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: 5,
                bgcolor: alpha(statusPal.main, 0.16),
                color: theme.palette.mode === 'dark' ? statusPal.light : statusPal.dark,
                fontSize: '0.72rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {status.label}
            </Box>
          )}
        </Box>

        {children != null && <Box sx={{ mt: 1.25 }}>{children}</Box>}

        {hasFooter && (
          <>
            <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', my: 1.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>{footerLeft}</Box>
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {footerRight}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MobileEntityCard;
